import React, { useState, useCallback, useRef, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
  Grid,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';

import { selectMediaViewer } from '../../store/modules/media-viewer/selectors';
import {
  setCurrentIndexMediaViewer,
  setOpenMediaViewer,
  setCurrentDocumentPageMediaViewer,
} from '../../store/modules/media-viewer/actions';
import { useId } from '../../hooks/index';
import MediaPreview from './MediaPreview';
import NextAction from './actions/nextAction';
import PreviousAction from './actions/previousAction';
import CloseAction from './actions/closeAction';
import MediaPreviewStyles from './styles';
import keyCodeConverter from '../../utility/keyCodeConverter';
import PageSelect from './actions/PageSelect';
import PreviewButton from './actions/PreviewButton';
import { DeferredActionContext } from '../../services/common/DeferredActionContext';

const changePreviewMediaDelay = 500;

const MediaPreviewModal = () => {
  const classes = MediaPreviewStyles();

  const refPreviewContent = useRef<null | HTMLDivElement>(null);
  const id = useId();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { open, mediaList, documentPageList, currentIndex, currentDocumentPage } = useSelector(selectMediaViewer);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | 'up' | 'down'>('left');
  const [isChangePreviewMedia, setIsChangePreviewMedia] = useState<number | null>(null);
  const media = mediaList[currentIndex];
  const { processActions } = useContext(DeferredActionContext);

  const onClose = useCallback(() => {
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
      setIsChangePreviewMedia(null);
    }
    dispatch(setOpenMediaViewer(false));
    setSlideDirection('left');
  }, [dispatch, setSlideDirection, isChangePreviewMedia, setIsChangePreviewMedia]);

  const selectPageHandler = useCallback(() => {
    processActions((currentDocumentPage + 1));
    onClose();
  }, [processActions, onClose, currentDocumentPage]);

  const nextMedia = useCallback(() => {
    let nextIndex = currentIndex + 1;
    if (nextIndex === mediaList.length) {
      nextIndex = 0;
    }
    if (mediaList[nextIndex] === undefined) {
      onClose();
      return;
    }
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
    }
    setSlideDirection('right');
    const timeoutId = window.setTimeout(
      () => {
        dispatch(setCurrentIndexMediaViewer(nextIndex));
        setSlideDirection('left');
        setIsChangePreviewMedia(null);
      },
      changePreviewMediaDelay,
    );
    setIsChangePreviewMedia(timeoutId);
  }, [
    dispatch,
    currentIndex,
    mediaList,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    onClose,
  ]);

  const previousMedia = useCallback(() => {
    let previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
      previousIndex = mediaList.length - 1;
    }
    if (mediaList[previousIndex] === undefined) {
      onClose();
      return;
    }
    setSlideDirection('left');
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
    }
    const timeoutId = window.setTimeout(
      () => {
        dispatch(setCurrentIndexMediaViewer(previousIndex));
        setSlideDirection('right');
        setIsChangePreviewMedia(null);
      },
      changePreviewMediaDelay,
    );
    setIsChangePreviewMedia(timeoutId);
  }, [
    dispatch,
    currentIndex,
    mediaList,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    onClose,
  ]);

  const nextDocumentPage = useCallback(() => {
    if (media.typeId === 4) {
      let nextIndex = currentDocumentPage + 1;
      if (nextIndex === documentPageList.length) {
        nextIndex = 0;
      }
      if (isChangePreviewMedia !== null) {
        window.clearTimeout(isChangePreviewMedia);
      }
      setSlideDirection('down');
      const timeoutId = window.setTimeout(
        () => {
          setSlideDirection('up');
          setIsChangePreviewMedia(null);
          dispatch(setCurrentDocumentPageMediaViewer(nextIndex));
        },
        changePreviewMediaDelay,
      );
      setIsChangePreviewMedia(timeoutId);
    }
  }, [
    dispatch,
    media,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    documentPageList,
    currentDocumentPage,
  ]);

  const previousDocumentPage = useCallback(() => {
    if (media.typeId === 4) {
      let previousIndex = currentDocumentPage - 1;
      if (previousIndex < 0) {
        previousIndex = documentPageList.length - 1;
      }
      if (isChangePreviewMedia !== null) {
        window.clearTimeout(isChangePreviewMedia);
      }
      setSlideDirection('up');
      const timeoutId = window.setTimeout(
        () => {
          setSlideDirection('down');
          setIsChangePreviewMedia(null);
          dispatch(setCurrentDocumentPageMediaViewer(previousIndex));
        },
        changePreviewMediaDelay,
      );
      setIsChangePreviewMedia(timeoutId);
    }
  }, [
    dispatch,
    media,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    documentPageList,
    currentDocumentPage,
  ]);

  const swipeable = useSwipeable({
    onSwipedLeft: nextMedia,
    onSwipedRight: previousMedia,
    onSwipedUp: nextDocumentPage,
    onSwipedDown: previousDocumentPage,
    delta: 100,
  });

  const keyHandler = useCallback((e: React.KeyboardEvent) => {
    switch (e.keyCode) {
      case keyCodeConverter('esc'):
        e.preventDefault();
        onClose();
        break;
      case keyCodeConverter('upArrow'):
        e.preventDefault();
        previousDocumentPage();
        break;
      case keyCodeConverter('downArrow'):
        e.preventDefault();
        nextDocumentPage();
        break;
      case keyCodeConverter('rightArrow'):
        e.preventDefault();
        nextMedia();
        break;
      case keyCodeConverter('leftArrow'):
        e.preventDefault();
        previousMedia();
        break;
      default:
        break;
    }
  }, [
    onClose,
    previousMedia,
    nextMedia,
    previousDocumentPage,
    nextDocumentPage,
  ]);

  if (media === undefined && mediaList.length > 0) {
    nextMedia();
  } else if (mediaList.length === 0 || media === undefined) {
    if (open) {
      onClose();
    }
    return null;
  }

  return (
    <Dialog
      open={open}
      disableBackdropClick
      fullWidth
      aria-labelledby={id}
      maxWidth={compact ? 'xl' : 'lg'}
      disablePortal
      BackdropProps={{
        className: classes.backdrop,
      }}
      PaperProps={{
        className: classes.paperRoot,
      }}
      style={{ zIndex: 2147483200 }}
      onKeyDown={keyHandler}
      className={classes.noselect}
    >
      <Grid container className={classes.header} id={id}>
        <Grid item>
          <CloseAction onClick={onClose} />
        </Grid>
        <Grid item style={{ maxWidth: 'calc(100% - 40px)' }}>
          <div className={classes.title}>{media.title}</div>
        </Grid>
        <Grid container justify="flex-end" spacing={1} style={{ marginTop: 10 }}>
          {media.typeId === 4
          && (
            <Grid item container justify="space-between">
              <Grid item>
                <PreviewButton onClick={selectPageHandler} title="Select Page" />
              </Grid>
              <Grid item>
                <PageSelect />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <DialogContent className={classes.content}>
        {mediaList.length > 1 && <PreviousAction onClick={previousMedia} />}
        <div className={classes.mediaPreviewContainer} ref={refPreviewContent}>
          <MediaPreview
            refPreviewContent={refPreviewContent}
            media={media}
            swipeable={swipeable}
            slideDirection={slideDirection}
            isChangePreviewMedia={isChangePreviewMedia}
            key={`${media.url}-${media.id}`}
          />
        </div>
        {mediaList.length > 1 && <NextAction onClick={nextMedia} />}
      </DialogContent>
    </Dialog>
  );
};

export default MediaPreviewModal;
